import { useState, useEffect } from 'react';
import { computeDEA } from './deaModel';
import { supabase } from '../supabaseClient';
import Visualization from './Visualization';

export default function DEAAnalysis() {
  const [datasets, setDatasets] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [modelType, setModelType] = useState('BCC');
  const [orientation, setOrientation] = useState('input');
  const [results, setResults] = useState([]);
  const [scaleResults, setScaleResults] = useState([]);
  const [inspectedDMU, setInspectedDMU] = useState(null);
  
  // Tab State
  const [activeTab, setActiveTab] = useState('results'); // 'results', 'charts', 'notes'

  useEffect(() => {
    const fetchDatasets = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.from('datasets').select('*').eq('user_id', user.id);
        setDatasets(data || []);
      }
    };
    fetchDatasets();
  }, []);

  const runDEA = (tempModelType = modelType, tempOrientation = orientation) => {
    if (!selectedData || !selectedData.data || !selectedData.data.dmus) return [];
    return computeDEA(selectedData.data.dmus, tempModelType, tempOrientation);
  };

  const handleComputeDEA = () => {
    const res = runDEA();
    setResults(res);
    setScaleResults([]); 
    setInspectedDMU(null);
    setActiveTab('results'); // Switch to results tab
  };

  const handleComputeScale = () => {
    const ccrRes = runDEA('CCR', 'input');
    const bccRes = runDEA('BCC', 'input');
    const scale = ccrRes.map((ccr, i) => ({
      dmu: ccr.dmu,
      ccrEff: ccr.efficiency,
      bccEff: bccRes[i].efficiency,
      scaleEff: (ccr.efficiency / bccRes[i].efficiency).toFixed(4),
      returnsToScale: (ccr.efficiency / bccRes[i].efficiency) >= 0.999 ? 'CRS' : 'VRS',
    }));
    setScaleResults(scale);
    setResults([]);
    setActiveTab('results');
  };

  // --- STYLES FOR TABS ---
  const tabStyle = (isActive) => ({
    padding: '10px 20px',
    cursor: 'pointer',
    borderBottom: isActive ? '3px solid #007bff' : '1px solid #ccc',
    color: isActive ? '#007bff' : '#666',
    fontWeight: isActive ? 'bold' : 'normal',
    background: 'none',
    border: 'none',
    marginRight: '5px'
  });

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial' }}>
      
      {/* CONTROLS */}
      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <select 
            onChange={(e) => {
                const selectedId = e.target.value;
                const dataset = datasets.find(d => String(d.id) === selectedId); 
                setSelectedData(dataset);
                setResults([]); 
                setScaleResults([]);
            }} 
            style={{ marginRight: '10px', padding: '5px' }}
        >
            <option value="">Select Dataset</option>
            {datasets.map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
        </select>

        <select value={modelType} onChange={(e) => setModelType(e.target.value)} style={{ marginRight: '10px', padding: '5px' }}>
            <option value="CCR">CCR (CRS)</option>
            <option value="BCC">BCC (VRS)</option>
        </select>

        <select value={orientation} onChange={(e) => setOrientation(e.target.value)} style={{ marginRight: '10px', padding: '5px' }}>
            <option value="input">Input-Oriented</option>
            <option value="output">Output-Oriented</option>
        </select>

        <button onClick={handleComputeDEA} disabled={!selectedData} style={{ padding: '6px 12px', cursor: 'pointer' }}>Run Analysis</button>
        <button onClick={handleComputeScale} disabled={!selectedData} style={{ marginLeft: '10px', padding: '6px 12px', cursor: 'pointer' }}>Scale Efficiency</button>
      </div>

      {/* TABS HEADER */}
      {(results.length > 0 || scaleResults.length > 0) && (
        <div style={{ borderBottom: '1px solid #ccc', marginBottom: '20px' }}>
            <button onClick={() => setActiveTab('results')} style={tabStyle(activeTab === 'results')}>Results Table</button>
            <button onClick={() => setActiveTab('charts')} style={tabStyle(activeTab === 'charts')}>Visualizations</button>
            <button onClick={() => setActiveTab('notes')} style={tabStyle(activeTab === 'notes')}>Educational Notes</button>
        </div>
      )}

      {/* TAB CONTENT: RESULTS TABLE */}
      {activeTab === 'results' && results.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Results ({modelType} - {orientation}-oriented)</h3>
              <small style={{ color: '#007bff' }}>👇 Click a row to see Peer & Target details</small>
            </div>
            
            <table border="1" cellPadding="8" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem', cursor: 'pointer' }}>
            <thead style={{ background: '#eee' }}>
              <tr><th>DMU</th><th>Efficiency</th><th>Status</th><th>Peers (λ)</th><th>Input Slacks</th><th>Output Slacks</th><th>Target Inputs</th><th>Target Outputs</th></tr>
            </thead>
            <tbody>
              {results.map(res => (
                <tr 
                  key={res.dmu} 
                  onClick={() => {
                    setInspectedDMU(res);
                    setActiveTab('charts'); // Optional: Auto-switch to charts tab
                  }}
                  style={{ 
                    background: inspectedDMU?.dmu === res.dmu ? '#cce5ff' : res.solverFailed ? '#f8d7da' : (res.isEfficient ? '#d4edda' : '#fff'),
                    border: inspectedDMU?.dmu === res.dmu ? '2px solid #007bff' : '1px solid #ccc'
                  }}
                  title="Click to view charts for this DMU"
                >
                  <td>{res.dmu}</td>
                  <td><strong>{res.solverFailed ? '—' : res.efficiency}</strong></td>
                  <td>{res.solverFailed ? 'Not solved' : res.isEfficient ? 'Efficient' : 'Inefficient'}</td>
                  <td>{res.peers.map(p => `${p.name} (${p.lambda.toFixed(2)})`).join(', ') || '-'}</td>
                  <td>{res.slacks.inputs.join(', ')}</td><td>{res.slacks.outputs.join(', ')}</td>
                  <td>{res.targets.inputs.join(', ')}</td><td>{res.targets.outputs.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'results' && scaleResults.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <h3>Scale Efficiency Results</h3>
            <table border="1" cellPadding="8" style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead style={{ background: '#eee' }}>
                <tr><th>DMU</th><th>CCR (Total)</th><th>BCC (Technical)</th><th>Scale Eff</th><th>Return to Scale</th></tr>
                </thead>
                <tbody>
                {scaleResults.map(res => (
                    <tr key={res.dmu}>
                    <td>{res.dmu}</td><td>{res.ccrEff}</td><td>{res.bccEff}</td><td>{res.scaleEff}</td><td>{res.returnsToScale}</td>
                    </tr>
                ))}
                </tbody>
            </table>
          </div>
      )}

      {/* TAB CONTENT: CHARTS */}
      {activeTab === 'charts' && selectedData && (
          <Visualization 
            results={results.length > 0 ? results : []} 
            scaleResults={scaleResults.length > 0 ? scaleResults : []} 
            dmus={selectedData.data.dmus}
            inspectedDMU={inspectedDMU}
            orientation={orientation}
          />
      )}

      {/* TAB CONTENT: NOTES */}
      {activeTab === 'notes' && (
        <div style={{ padding: '20px', background: '#f0f8ff', borderRadius: '8px', lineHeight: '1.6' }}>
            <h4>Understanding DEA Results</h4>
            <ul>
            <li><strong>Efficiency Score:</strong> 1.0000 indicates the unit is efficient (on the frontier). Values below 1 indicate inefficiency. Scores are reported on a single 0–1 scale in both orientations (output-oriented φ is shown as 1/φ), so they are directly comparable.</li>
            <li><strong>Peers (Benchmarks):</strong> The efficient units that the inefficient unit is compared to. The lambda (λ) value represents the weight of that peer in the reference set.</li>
            <li><strong>Slacks:</strong> The extra amount inputs could be reduced (or outputs increased) <em>after</em> reaching the efficiency frontier to become fully efficient.</li>
            <li><strong>Not solved:</strong> The built-in numeric solver could not produce a valid solution for that unit (it can stall on highly degenerate data). Its score is shown as “—” rather than a misleading 1.0000, and its targets are left at current levels.</li>
            <li><strong>Scale Efficiency:</strong> Measures if the unit is operating at the optimal size. If SE &lt; 1, the unit is either too small (Increasing Returns to Scale) or too large (Decreasing Returns to Scale).</li>
            </ul>
        </div>
      )}
    </div> 
  );
}